/* Task rules — STORY-025 and STORY-026.
 *
 * ── overdue is computed in the VIEWER's timezone, never UTC ─────────────────
 * `glossary.md` owns this rule and it is the one most easily got wrong, because
 * getting it wrong is invisible for most of the day. A Task due today is not
 * overdue because UTC has rolled past midnight, and a Member in Istanbul must
 * not watch their own task turn red mid-afternoon because a server is west of
 * them.
 *
 * The consequence, accepted deliberately on 2026-08-12: two Members in two
 * zones can disagree about whether a Task is late, by at most one day. That is
 * the better half of the trade — the alternative makes somebody's own task
 * wrong on their own screen.
 *
 * ── a date is a date ────────────────────────────────────────────────────────
 * Nothing here parses a date string with `new Date(...)`. The implicit zone of
 * a bare `YYYY-MM-DD` differs between runtimes and `lang-typescript.md` bans it
 * outright; comparison is on the string, which for ISO dates is the same
 * ordering as the calendar and needs no zone at all.
 */
import { TASK_PRIORITIES } from './task-status.js';
/**
 * The calendar day it is, where the reader is standing.
 *
 * `Intl` with an explicit zone, formatted to `en-CA` because that locale's
 * short date IS `YYYY-MM-DD` — which makes the result directly comparable to a
 * stored `date` without parsing either side. Doing this with `getDate()` and
 * friends would silently use the runtime's own zone, which is exactly the bug.
 */
export function todayIn(timeZone, now) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
/**
 * Whether a Task is late, for a reader in this zone at this instant.
 *
 * Three ways to be not-overdue, and all three are the story's words:
 * no due date, a due date not yet passed, and a Task that is finished or
 * called off whatever its date.
 */
export function isOverdue(task, timeZone, now) {
    if (task.dueOn === null)
        return false;
    if (task.status === 'completed' || task.status === 'cancelled')
        return false;
    /* String comparison, deliberately. Both sides are `YYYY-MM-DD`, so this is
     * the calendar ordering with no parse and no zone assumption anywhere. */
    return task.dueOn < todayIn(timeZone, now);
}
/** High first. Used for sorting, never rendered — priority names are keys. */
const PRIORITY_RANK = {
    high: 0,
    medium: 1,
    low: 2,
};
/**
 * The story's sort, in its own order: overdue first, then by due date
 * ascending, then by priority descending. Tasks with no due date sort last.
 *
 * Overdue leads because the list's entire job is telling somebody what needs
 * doing, and a late Task buried under three on-time ones is a list that has
 * failed at that. Undated last for the same reason inverted: a Task nobody has
 * committed to a day is the one least likely to be urgent.
 */
export function compareTasks(timeZone, now) {
    return (a, b) => {
        const lateA = isOverdue(a, timeZone, now);
        const lateB = isOverdue(b, timeZone, now);
        if (lateA !== lateB)
            return lateA ? -1 : 1;
        /* Undated last, whichever direction the dated ones are going. */
        if (a.dueOn === null && b.dueOn !== null)
            return 1;
        if (a.dueOn !== null && b.dueOn === null)
            return -1;
        if (a.dueOn !== null && b.dueOn !== null && a.dueOn !== b.dueOn) {
            return a.dueOn < b.dueOn ? -1 : 1;
        }
        return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    };
}
/**
 * How far ahead a due date has to be before it is worth asking about.
 *
 * Twelve months, from the story. It catches the mistyped year — 2062 for 2026
 * — which is the class of error a spreadsheet's date column accepts silently,
 * without forbidding a date that is genuinely distant.
 */
export const FAR_FUTURE_MONTHS = 12;
/**
 * Whether a due date is far enough out to warrant a confirmation.
 *
 * A warning, never a refusal: the story is explicit, and a refusal here would
 * make a real long-lead Task impossible to record.
 *
 * Month arithmetic in UTC on the parsed parts, not on a constructed local
 * `Date` — the threshold is "twelve months from the reader's today", and both
 * sides are calendar dates.
 */
export function isFarFuture(dueOn, today) {
    const [year, month, day] = today.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined)
        return false;
    /* `Date.UTC` normalises the overflow: month 13 becomes January of the next
     * year, and 31 February becomes 3 March. Both are fine here — this is a
     * threshold, not a date anybody sees. */
    const threshold = new Date(Date.UTC(year, month - 1 + FAR_FUTURE_MONTHS, day));
    return dueOn > todayIn('UTC', threshold);
}
export { TASK_PRIORITIES };
//# sourceMappingURL=task.js.map